(function exposeTitanRecommendationEngine(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.TitanRecommendationEngine = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function createTitanRecommendationEngine() {
  'use strict';

  const GPU_SPECS = Object.freeze({
    RTX5080: Object.freeze({ score: 33000, vramGb: 16 }),
    RTX5070TI: Object.freeze({ score: 27700, vramGb: 16 }),
    RX9070XT: Object.freeze({ score: 29900, vramGb: 16 }),
    RX9070: Object.freeze({ score: 26300, vramGb: 16 }),
    RTX5070: Object.freeze({ score: 22500, vramGb: 12 }),
    RTX5060TI16GB: Object.freeze({ score: 15200, vramGb: 16 }),
    RTX5060TI8GB: Object.freeze({ score: 15200, vramGb: 8 }),
    RTX5060TI: Object.freeze({ score: 15200, vramGb: 8 }),
    RX9060XT16GB: Object.freeze({ score: 16200, vramGb: 16 }),
    RX9060XT8GB: Object.freeze({ score: 16200, vramGb: 8 }),
    RX9060XT: Object.freeze({ score: 16200, vramGb: 8 }),
    RTX5060: Object.freeze({ score: 13900, vramGb: 8 }),
    RTX5050: Object.freeze({ score: 10259, vramGb: 8 }),
    RTX4070TI: Object.freeze({ score: 23500, vramGb: 12 }),
    RTX4070: Object.freeze({ score: 18500, vramGb: 12 }),
    RTX3080TI: Object.freeze({ score: 19000, vramGb: 12 }),
    RTX3080: Object.freeze({ score: 17500, vramGb: 10 }),
    RTX4060TI8GB: Object.freeze({ score: 13800, vramGb: 8 }),
    RTX4060TI: Object.freeze({ score: 13800, vramGb: 8 }),
    RTX4060: Object.freeze({ score: 10500, vramGb: 8 }),
    RTX3070TI: Object.freeze({ score: 15000, vramGb: 8 }),
    RTX3070: Object.freeze({ score: 13500, vramGb: 8 }),
    RTX306012GB: Object.freeze({ score: 8800, vramGb: 12 }),
    RTX3060: Object.freeze({ score: 8800, vramGb: 12 }),
    RTX3050: Object.freeze({ score: 6278, vramGb: 6 }),
  });

  const CPU_OPTIONS = Object.freeze([
    'Ryzen 7 9800X3D',
    'Ryzen 7 7800X3D',
    'Ryzen 7 9700X',
    'Ryzen 7 7700',
    'Ryzen 7 5700X',
    'Ryzen 5 5500',
  ]);

  function normalizeGpuName(value) {
    return String(value || '')
      .toUpperCase()
      .replace(/GEFORCE|NVIDIA|RADEON/g, '')
      .replace(/[^A-Z0-9]/g, '');
  }

  function gpuDetails(value) {
    const normalized = normalizeGpuName(value);
    const direct = GPU_SPECS[normalized];
    if (direct) return direct;
    const key = Object.keys(GPU_SPECS).find((candidate) => normalized.includes(candidate));
    return key ? GPU_SPECS[key] : null;
  }

  function normalizeGameKey(value) {
    return String(value || '').trim().toLowerCase();
  }

  function resolveMinimumScore(baseMinimum, gameIds, gameMinimums, profile) {
    const base = Math.max(0, Number(baseMinimum) || 0);
    if (!Array.isArray(gameIds) || !gameMinimums || typeof gameMinimums !== 'object') return base;
    const normalizedMinimums = Object.entries(gameMinimums).reduce(
      (minimums, [gameId, value]) => ({ ...minimums, [normalizeGameKey(gameId)]: value }),
      Object.create(null),
    );
    return gameIds.reduce((minimum, gameId) => {
      const requirement = normalizedMinimums[normalizeGameKey(gameId)];
      const value = requirement && typeof requirement === 'object'
        ? requirement[profile]
        : requirement;
      const score = Number(value);
      return Number.isFinite(score) && score > minimum ? score : minimum;
    }, base);
  }

  function cleanText(value, maxLength) {
    return typeof value === 'string'
      ? value.replace(/[<>]/g, '').replace(/\s+/g, ' ').trim().slice(0, maxLength)
      : '';
  }

  function normalizeProduct(product) {
    if (!product || typeof product !== 'object') return null;
    const details = gpuDetails(product.key);
    const price = Number(product.price);
    const condition = product.condition === 'refurb' ? 'refurb' : product.condition === 'new' ? 'new' : '';
    if (
      typeof product.id !== 'string' ||
      !product.id ||
      typeof product.key !== 'string' ||
      !details ||
      typeof product.url !== 'string' ||
      !product.url ||
      !Number.isFinite(price) ||
      price <= 0 ||
      !condition ||
      product.available === false
    ) {
      return null;
    }
    return Object.freeze({
      ...product,
      id: product.id,
      key: cleanText(product.key, 40),
      title: cleanText(product.title, 220),
      price,
      condition,
      gpuScore: details.score,
      vramGb: details.vramGb,
    });
  }

  function conditionMatches(product, condition) {
    return product.condition === 'new';
  }

  function selectCandidates(catalog, criteria) {
    const source = criteria && typeof criteria === 'object' ? criteria : {};
    const minScore = Math.max(0, Number(source.minScore) || 0);
    const minVramGb = Math.max(0, Number(source.minVramGb) || 0);
    const budget = source.budgetUnlimited ? Number.POSITIVE_INFINITY : Number(source.budget);
    const priceCeiling = Number.isFinite(budget) && budget > 0 ? budget : Number.POSITIVE_INFINITY;
    const condition = 'new';
    const capable = (Array.isArray(catalog) ? catalog : [])
      .map(normalizeProduct)
      .filter(Boolean)
      .filter(
        (product) =>
          product.gpuScore >= minScore &&
          product.vramGb >= minVramGb &&
          conditionMatches(product, condition),
      )
      .sort((left, right) => left.price - right.price || right.gpuScore - left.gpuScore);
    return Object.freeze({
      candidates: Object.freeze(capable.filter((product) => product.price <= priceCeiling)),
      overBudget: Object.freeze(capable.filter((product) => product.price > priceCeiling)),
    });
  }

  function productCpu(product) {
    const title = String(product && product.title || '');
    return CPU_OPTIONS.find((cpu) => title.toUpperCase().includes(cpu.toUpperCase())) || '商品ページで確認';
  }

  function resultCpu(value, product) {
    const cleaned = cleanText(value, 40);
    return CPU_OPTIONS.find((cpu) => cleaned.toUpperCase().includes(cpu.toUpperCase())) || productCpu(product);
  }

  function resultMemory(value, cpu) {
    const match = String(value || '').match(/(?:16|32|64)\s*GB/i);
    const requested = match ? Number(match[0].match(/\d+/)[0]) : 16;
    const isAm4 = /5500|5700X/i.test(cpu);
    return `${isAm4 ? Math.min(requested, 32) : requested}GB`;
  }

  function hydrateResults(rawItems, candidates) {
    const products = (Array.isArray(candidates) ? candidates : []).map(normalizeProduct).filter(Boolean);
    return Object.freeze(
      (Array.isArray(rawItems) ? rawItems : []).reduce((items, raw) => {
        if (!raw || typeof raw !== 'object' || typeof raw.productId !== 'string') return items;
        if (items.some((item) => item.productId === raw.productId)) return items;
        const product = products.find((candidate) => candidate.id === raw.productId);
        if (!product) return items;
        const cpu = resultCpu(raw.cpu, product);
        const budgetDelta = Math.max(0, Number(product.budgetDelta) || 0);
        return [
          ...items,
          Object.freeze({
            productId: product.id,
            rank: cleanText(raw.rank, 24) || (items.length === 0 ? 'best' : 'alt'),
            label: cleanText(raw.label, 36),
            gpu: product.key,
            price: product.price,
            cpu,
            mem: resultMemory(raw.mem, cpu),
            /* 2026-08-16: AIが返した最終構成(ssd)と合計金額(total)を保持する。
               従来はここで捨てていたため、画面が標準構成の価格を出していた。
               total は掲載価格以上・掲載価格の3倍以内のみ採用(異常値やハルシネーション対策)。 */
            ssd: cleanText(raw.ssd, 24),
            total: (function(){
              const t = Number(raw.total);
              if (!Number.isFinite(t)) return 0;
              if (t < product.price) return 0;
              if (t > product.price * 3) return 0;
              return Math.round(t);
            })(),
            cond: '新品',
            budgetDelta,
            why: cleanText(raw.why, 80),
            tip: cleanText(raw.tip, 140),
            tags: Object.freeze(
              (Array.isArray(raw.tags) ? raw.tags : [])
                .map((tag) => cleanText(tag, 24))
                .filter(Boolean)
                .slice(0, 3),
            ),
          }),
        ];
      }, []),
    );
  }

  function fallbackResults(candidates) {
    const products = (Array.isArray(candidates) ? candidates : []).map(normalizeProduct).filter(Boolean);
    if (!products.length) return Object.freeze([]);
    const priceOrdered = [...products].sort(
      (left, right) => left.price - right.price || right.gpuScore - left.gpuScore,
    );
    const unique = priceOrdered.slice(0, 3);
    return hydrateResults(
      unique.map((product, index) => ({
        productId: product.id,
        rank: index === 0 ? 'best' : index === 1 ? 'value' : 'alt',
        why: index === 0 ? '条件内で性能を優先' : index === 1 ? '必要性能と価格を両立' : '別の選択肢',
      })),
      products,
    );
  }

  function completeResults(rawItems, selectableCandidates, fallbackCandidates, maximumItems = 3) {
    const limit = Math.min(3, Math.max(1, Number(maximumItems) || 3));
    const selected = hydrateResults(rawItems, selectableCandidates);
    const fallback = fallbackResults(fallbackCandidates);
    return Object.freeze(
      [...selected, ...fallback]
        .filter(
          (item, index, items) =>
            items.findIndex((candidate) => candidate.productId === item.productId) === index,
        )
        .slice(0, limit),
    );
  }

  /* 比較候補に、ほぼ同価格で明確に速い同用途帯の商品がある場合は下位GPUを残さない。
     AIの本命は維持し、比較枠だけを実商品データで補正する。 */
  function upgradeDominatedComparisons(items, candidates, options) {
    const source = Array.isArray(items) ? items.filter(Boolean) : [];
    const products = (Array.isArray(candidates) ? candidates : []).map(normalizeProduct).filter(Boolean);
    if (source.length < 2 || !products.length) return Object.freeze([...source]);

    const reservedIds = new Set(source.map((item) => item.productId));
    const chosenFamilies = new Set();
    const hasExplicitPrimary = source.some((item) => item.rank === 'best');
    const refined = source.map((item, index) => {
      const currentFamily = normalizeGpuName(item.gpu).replace(/(?:8|16)GB$/, '');
      const isPrimary = item.rank === 'best' || (!hasExplicitPrimary && index === 0);
      const replaceUnlimitedRtx5050 = isPrimary && currentFamily === 'RTX5050' && !!(options && options.budgetUnlimited);
      if (isPrimary && !replaceUnlimitedRtx5050) {
        chosenFamilies.add(currentFamily);
        return item;
      }
      const currentProduct = products.find((product) => product.id === item.productId);
      const currentGpu = gpuDetails(item.gpu);
      if (!currentProduct || !currentGpu) {
        chosenFamilies.add(currentFamily);
        return item;
      }

      const duplicateFamily = chosenFamilies.has(currentFamily);
      const forceRtx5050Upgrade = currentFamily === 'RTX5050' && !!(options && options.budgetUnlimited);
      const maximumPrice = duplicateFamily || forceRtx5050Upgrade
        ? Math.max(currentProduct.price * 1.25, currentProduct.price + 40000)
        : Math.max(currentProduct.price * 1.1, currentProduct.price + 15000);
      const requestedCpu = cpuModel(item.cpu);
      const replacements = products
        .filter((product) => {
          const productFamily = normalizeGpuName(product.key).replace(/(?:8|16)GB$/, '');
          if (reservedIds.has(product.id) || chosenFamilies.has(productFamily) || product.price > maximumPrice) return false;
          const productGpu = gpuDetails(product.key);
          if (!productGpu) return false;
          if (duplicateFamily && productGpu.score < currentGpu.score * 0.85) return false;
          if (forceRtx5050Upgrade && productGpu.score <= currentGpu.score) return false;
          if (!duplicateFamily && !forceRtx5050Upgrade && productGpu.score < currentGpu.score * 1.15) return false;
          return !requestedCpu || cpuAddonPrice(product, requestedCpu) !== null;
        });
      replacements.sort((left, right) => duplicateFamily
        ? Math.abs(left.gpuScore - currentGpu.score) - Math.abs(right.gpuScore - currentGpu.score) || left.price - right.price
        : right.gpuScore - left.gpuScore || left.price - right.price);
      const replacement = replacements[0];
      if (!replacement) {
        chosenFamilies.add(currentFamily);
        return item;
      }

      reservedIds.add(replacement.id);
      chosenFamilies.add(normalizeGpuName(replacement.key).replace(/(?:8|16)GB$/, ''));
      return hydrateResults([{
        productId: replacement.id,
        rank: item.rank,
        cpu: item.cpu,
        mem: item.mem,
        ssd: item.ssd,
        why: '価格差を抑えて性能を上げる',
        tip: item.tip,
        tags: item.tags,
      }], products)[0] || item;
    });
    return Object.freeze(refined);
  }

  const CPU_TIERS = Object.freeze([
    Object.freeze({ pattern: /9800X3D/i, tier: 100 }),
    Object.freeze({ pattern: /9700X|7800X3D/i, tier: 95 }),
    Object.freeze({ pattern: /7700/i, tier: 85 }),
    Object.freeze({ pattern: /5700X/i, tier: 70 }),
    Object.freeze({ pattern: /5500/i, tier: 55 }),
  ]);

  function resultTotal(item) {
    const configured = Number(item && item.total);
    if (Number.isFinite(configured) && configured > 0) return configured;
    const listed = Number(item && item.price);
    return Number.isFinite(listed) && listed > 0 ? listed : Number.POSITIVE_INFINITY;
  }

  function cpuTier(value) {
    const text = String(value || '');
    const match = CPU_TIERS.find((entry) => entry.pattern.test(text));
    return match ? match.tier : 0;
  }

  function capacityGb(value) {
    const match = String(value || '').toUpperCase().match(/(\d+(?:\.\d+)?)\s*(TB|GB)/);
    if (!match) return 0;
    const amount = Number(match[1]);
    return match[2] === 'TB' ? amount * 1024 : amount;
  }

  function sameOrBetterComponent(candidateValue, currentValue, measure) {
    const candidateText = String(candidateValue || '').trim();
    const currentText = String(currentValue || '').trim();
    if (candidateText === currentText) return true;
    const candidateScore = measure(candidateValue);
    const currentScore = measure(currentValue);
    return candidateScore > 0 && currentScore > 0 && candidateScore >= currentScore;
  }

  /* AIのbestを尊重しつつ「明確な上位互換」があれば本命へ昇格する。
     予算上限なしでも、価格上昇より性能上昇が大きい現実的な範囲だけを対象にする。 */
  function choosePrimaryResult(items, budgetCeiling) {
    const source = Array.isArray(items) ? items.filter(Boolean) : [];
    const aiPrimary = source.find((item) => item.rank === 'best') || source[0] || null;
    const ceiling = Number(budgetCeiling);
    if (!aiPrimary || ceiling <= 0) return aiPrimary;

    const unlimited = !Number.isFinite(ceiling);
    if (unlimited) {
      const finiteTotals = source.map(resultTotal).filter(Number.isFinite);
      const lowestTotal = finiteTotals.length ? Math.min(...finiteTotals) : Number.POSITIVE_INFINITY;
      const sensibleCeiling = lowestTotal * 1.5;
      if (resultTotal(aiPrimary) > sensibleCeiling) {
        const sensible = source
          .filter((candidate) => resultTotal(candidate) <= sensibleCeiling)
          .sort((left, right) =>
            gpuDetails(right.gpu).score - gpuDetails(left.gpu).score ||
            cpuTier(right.cpu) - cpuTier(left.cpu) ||
            resultTotal(left) - resultTotal(right),
          );
        if (sensible.length) return sensible[0];
      }
    }

    const currentGpu = gpuDetails(aiPrimary.gpu).score;
    const currentTotal = resultTotal(aiPrimary);
    const upgrades = source.filter((candidate) => {
      const candidateTotal = resultTotal(candidate);
      const candidateGpu = gpuDetails(candidate.gpu).score;
      if (candidate === aiPrimary || (!unlimited && candidateTotal > ceiling)) return false;
      if (candidateGpu <= currentGpu) return false;
      if (!sameOrBetterComponent(candidate.cpu, aiPrimary.cpu, cpuTier)) return false;
      if (!sameOrBetterComponent(candidate.mem, aiPrimary.mem, capacityGb)) return false;
      if (aiPrimary.ssd && !sameOrBetterComponent(candidate.ssd, aiPrimary.ssd, capacityGb)) return false;
      if (unlimited) {
        if (!Number.isFinite(currentTotal) || !Number.isFinite(candidateTotal)) return false;
        const priceRatio = candidateTotal / currentTotal;
        const performanceRatio = candidateGpu / currentGpu;
        if (priceRatio > 1.45 || performanceRatio < 1.25 || performanceRatio < priceRatio) return false;
      }
      return true;
    });
    upgrades.sort((left, right) =>
      gpuDetails(right.gpu).score - gpuDetails(left.gpu).score ||
      cpuTier(right.cpu) - cpuTier(left.cpu) ||
      capacityGb(right.mem) - capacityGb(left.mem) ||
      resultTotal(left) - resultTotal(right),
    );
    return upgrades[0] || aiPrimary;
  }

  function cpuModel(value) {
    const match = String(value || '').toUpperCase().match(/\b(5500|5700X|7700|9700X)\b/);
    return match ? match[1] : '';
  }

  function cpuAddonPrice(product, model) {
    const addons = product && product.addons && product.addons.cpu;
    if (!model) return null;
    if (cpuModel(product && product.title) === model) return 0;
    if (!addons) return null;
    const key = Object.keys(addons).find((name) => cpuModel(name) === model);
    if (key) {
      const price = Number(addons[key]);
      return Number.isFinite(price) ? price : null;
    }
    return null;
  }

  /* 5500/7700は価格・GPUを守るためのフォールバックとして残し、
     同じ商品のまま上位CPUへ変更でき、ケース込みでも予算内ならだけ補正する。 */
  function preferBalancedCpu(item, product, options) {
    if (!item || !product || (options && options.gpuPriority)) return item;
    const currentModel = cpuModel(item.cpu);
    const targetModel = currentModel === '5500' ? '5700X' : currentModel === '7700' ? '9700X' : '';
    if (!targetModel) return item;

    const currentAddon = cpuAddonPrice(product, currentModel);
    const targetAddon = cpuAddonPrice(product, targetModel);
    if (targetAddon === null) return item;

    const currentTotal = resultTotal(item);
    if (!Number.isFinite(currentTotal)) return item;
    const delta = targetAddon - (currentAddon === null ? 0 : currentAddon);
    const nextTotal = currentTotal + delta;
    const projected = Number(options && options.projectedTotal);
    const projectedTotal = Number.isFinite(projected) ? projected + delta : nextTotal;
    const ceiling = Number(options && options.budgetCeiling);
    if (Number.isFinite(ceiling) && ceiling > 0 && projectedTotal > ceiling) return item;

    return Object.freeze({
      ...item,
      cpu: `Ryzen 7 ${targetModel}`,
      total: Math.round(nextTotal),
    });
  }

  return Object.freeze({
    normalizeGpuName,
    gpuDetails,
    resolveMinimumScore,
    selectCandidates,
    hydrateResults,
    fallbackResults,
    completeResults,
    upgradeDominatedComparisons,
    choosePrimaryResult,
    preferBalancedCpu,
  });
});
